import { Transaction } from "../../../ui/charts/SankeyDiagram";
import { MonthlyData } from "../../../ui/charts/MonthlyBarChart";
import { openLoadingWindow, renderReportIntoWindow, buildBroadcastHyperlink, fmtAsOf } from "./CustomDashboardUtils";

/**
 * BUSINESS CHART 대시보드
 * - Business Intelligence > Business Chart 탭의 Sankey 데이터와 Monthly(월별 선적 건수) 데이터를
 *   TotalSalesOperation5Year 와 같은 형태의 팝업 리포트로 그린다.
 * - baseType 이 "CUSTOMER" 면 Customer → Agent → Shipper, "AGENT" 면 Agent → Customer → Shipper 순으로 그린다.
 *   (기준 업체가 첫 레이어, 상대 업체가 가운데 레이어)
 * - Sankey 는 가운데 레이어를 물량 상위 SANKEY_MAX_MIDDLE 개로, 가운데 노드마다 Shipper 를
 *   상위 SANKEY_MAX_SHIPPERS_PER_MIDDLE 개까지만 연결한다. (아래 Top 표들은 전체 데이터 기준)
 * - 물결(링크)에 마우스를 올리면 #chart-tooltip 에 출발 → 도착 업체, 건수, Operation 별 건수를 보여준다.
 * - 드릴다운 링크는 buildBroadcastHyperlink 로 메인 창에 아래 메시지를 보낸다.
 *   ["BusinessChartToListView", opCode, asOfDate, customerId, nodeType, agentId, shipperName, blDate, baseType]
 *   customerId/agentId 중 기준 쪽은 항상 기준 업체 Id, 나머지 쪽은 클릭한 노드의 Id(클릭하지 않았으면 0).
 *   → 메인 창 브로드캐스트 리스너의 'BusinessChartToListView' 분기가 appContext.api.getSankeyNodeIds(...) 로 Id 목록을 받아
 *     extraWhere 필터로 리스트 화면을 연다(MonthlyBarChart / SankeyDiagram 의 navigateToList 와 동일).
 * - 마스트헤드의 "View as Customer/Agent" 링크는 같은 업체를 반대 기준으로 새 탭에 연다.
 *   ["BusinessChartDashboard", baseId, baseName, asOfDate, department, baseType] + (클릭 시점에 미리 연 탭 이름)
 *   → 'BusinessChartDashboard' 분기가 그 탭에 붙어서 그린다. 폴백 허용 플래그(8번째 요소)는 보내지 않으므로 요청 기준 그대로만 조회한다.
 * - 차트(막대·Sankey 노드) 클릭은 숨겨둔 링크 상자(#drill-m-*, #drill-n-*)의 <a> 를 click() 하는 방식이라
 *   buildBroadcastHyperlink 가 만드는 <a> 가 inline onclick/href 로 동작한다는 전제다.
 * - 기간 모드: Total Sales (Customer) Monthly / Quarterly 의 Business Chart 버튼은 날짜 자리(asOfDate)에
 *   "YYYY-MM-DD~YYYY-MM-DD" 형태로 기간을 넘긴다. splitPeriod 로 나눠 fromIso 가 있으면 5년 대신 그 기간으로 그린다.
 *   드릴다운(BusinessChartToListView)과 View as 링크의 날짜 자리에도 같은 형식을 그대로 넘겨 기간이 유지된다.
 *   날짜 하나만 오면(5 Years 리포트) 기존과 똑같이 5년으로 동작한다.
 */

/**
 * 날짜 자리 인자를 나눈다. "2026-01-01~2026-10-26" → { from: "2026-01-01", to: "2026-10-26" },
 * "2026-10-26" → { from: "", to: "2026-10-26" } (from 이 비면 서버는 기존처럼 to 기준 5년).
 * 리스너('BusinessChartDashboard', 'BusinessChartToListView')에서도 이 함수로 나눈다.
 */
export function splitPeriod(arg: string | undefined | null): { from: string; to: string } {
  const s = String(arg ?? "").trim();
  const i = s.indexOf("~");
  if (i < 0) return { from: "", to: s };
  return { from: s.slice(0, i).trim(), to: s.slice(i + 1).trim() };
}

const EMPTY_CUSTOMER_LABEL = "** Empty Customer **";
const EMPTY_AGENT_LABEL = "** Empty Agent **";
const EMPTY_SHIPPER_LABEL = "** Empty Shipper **";

/** Sankey 표시 제한 — 가운데 레이어(상대 업체)는 물량 상위 N개, 가운데 노드마다 오른쪽(Shipper)은 상위 M개까지만 연결 */
const SANKEY_MAX_MIDDLE = 20;
const SANKEY_MAX_SHIPPERS_PER_MIDDLE = 4;

const OPS: { code: string; key: "oi" | "oe" | "ai" | "ae" | "ot"; label: string; color: string }[] = [
  { code: "OI", key: "oi", label: "Ocean Import", color: "#3B82F6" },
  { code: "OE", key: "oe", label: "Ocean Export", color: "#0EA5A4" },
  { code: "AI", key: "ai", label: "Air Import", color: "#F59E0B" },
  { code: "AE", key: "ae", label: "Air Export", color: "#EF4444" },
  { code: "OT", key: "ot", label: "Other Shipment", color: "#8B5CF6" },
];

const COLORS = {
  navy: "#0A1F33",
  gold: "#B8862E",
  goldLight: "#E4C88A",
  steelBlue: "#3E6E96",
  up: "#2F6F52",
  down: "#9C3B3B",
  textMuted: "#4A5A66",
  faint: "#8A98A2",
  border: "#D8E0E3",
  rowAlt: "#FAFBFB",
  totalBg: "#FBF6EA",
  link: "#1668DC",
  linkHover: "#0D4FA8",
  grid: "#EEF2F4",
  yearBand: "#F4F6F8",
  sankeyLink: "#C8C8C8",
};

const SANKEY_PALETTE = [
  "#4A90D9", "#5B9BD5", "#6BA5E7",
  "#8B7EC8", "#9D8CD4", "#A890D3",
  "#5DC0B3", "#4DB6A5", "#45B39D",
  "#7BC47F", "#8FCE8F", "#5CB85C",
  "#F7C46C", "#E8B960", "#F0AD4E",
  "#F5A45D", "#F39C4D", "#E8893C",
  "#E86B5A", "#E57373", "#D9534F",
  "#E47D9E", "#E085A2", "#D5649A",
  "#B39DDB", "#CE93D8", "#BA68C8",
];

const STYLE = `
  @page { size: A4 landscape; margin: 13mm 12mm 15mm; }
  * { box-sizing: border-box; }
  body { font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; color:#101B26; margin:0; font-size:11.5px; background:#fff; }
  .dash-wrap { max-width:1120px; margin:0 auto; position:relative; }
  .page { margin-bottom:14px; }

  .masthead { background:${COLORS.navy}; color:#fff; padding:18px 24px 16px; border-radius:5px;
              display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:12px; }
  .masthead h1 { font-size:21px; margin:0 0 5px; font-weight:700; letter-spacing:-.01em; }
  .masthead .eyebrow { font-size:9.5px; letter-spacing:.14em; text-transform:uppercase; color:${COLORS.goldLight}; margin-bottom:6px; }
  .masthead .meta { font-size:10px; color:#B9C7D1; line-height:1.6; }
  .masthead .meta b { color:#fff; }
  .masthead .right { text-align:right; font-size:9.5px; color:#8FA7B8; line-height:1.5; }
  .masthead .basis { display:inline-block; margin-left:10px; padding:2px 9px; border-radius:10px; background:${COLORS.gold}; color:#fff;
                     font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; vertical-align:middle; }
  /* meta 줄 끝의 View as 버튼 — 네이비 위에 골드 채움 + 바깥 글로우 */
  .basis-switch { display:inline-flex; align-items:center; margin-left:12px; vertical-align:middle; border-radius:14px;
                  background:${COLORS.gold}; border:1px solid ${COLORS.goldLight}; box-shadow:0 0 0 3px rgba(184,134,46,.28);
                  transition:background .15s, box-shadow .15s; }
  .basis-switch a, .basis-switch a:link, .basis-switch a:visited { display:inline-flex; align-items:center; gap:6px; padding:3px 11px;
                  color:#fff !important; text-decoration:none; cursor:pointer;
                  font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:10.5px; font-weight:800; letter-spacing:.03em; }
  .basis-switch:hover { background:${COLORS.goldLight}; box-shadow:0 0 0 4px rgba(228,200,138,.4); }
  .basis-switch:hover a { color:${COLORS.navy} !important; }
  .basis-switch .sw-icon { font-size:12px; line-height:1; }

  .kpi-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-bottom:12px; }
  .kpi { background:#fff; border:1px solid ${COLORS.border}; border-top:3px solid ${COLORS.gold}; border-radius:5px; padding:10px 12px 9px; }
  .kpi.navy { border-top-color:${COLORS.navy}; }
  .kpi.blue { border-top-color:${COLORS.steelBlue}; }
  .kpi-label { font-size:9px; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.04em; font-weight:700; margin-bottom:5px; }
  .kpi-num { font-family:'Courier New',monospace; font-size:19px; font-weight:700; line-height:1; margin-bottom:5px; color:${COLORS.navy}; }
  .kpi-note { font-size:9px; color:${COLORS.textMuted}; line-height:1.5; }

  .insight { background:${COLORS.navy}; color:#EAF0F4; border-radius:5px; padding:12px 14px; font-size:10px; line-height:1.65; margin-bottom:12px; }
  .insight .tag { font-family:'Courier New',monospace; font-size:8.5px; letter-spacing:.1em; text-transform:uppercase; color:${COLORS.goldLight}; margin-bottom:5px; display:block; }
  .insight b { color:#fff; }

  .panel { background:#fff; border:1px solid ${COLORS.border}; border-radius:5px; padding:12px 14px; }
  .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .title { display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:9px; flex-wrap:wrap; }
  .title h2 { font-size:12px; margin:0; color:${COLORS.navy}; font-weight:700; text-transform:uppercase; letter-spacing:.03em; }
  .title span.sub { font-size:9px; color:${COLORS.textMuted}; font-weight:600; }

  .chart-legend { display:flex; gap:12px; font-size:8.6px; color:${COLORS.textMuted}; margin-bottom:5px; align-items:center; flex-wrap:wrap; }
  .chart-legend .sw { display:inline-block; width:9px; height:9px; border-radius:2px; margin-right:4px; vertical-align:middle; }
  .chart-legend .hint { margin-left:auto; color:${COLORS.faint}; font-style:italic; }
  .chart-scroll { overflow-x:auto; }
  .chart-scroll svg { display:block; }
  .axis { font-size:8.3px; fill:${COLORS.textMuted}; }
  .axis-year { font-size:9px; fill:${COLORS.navy}; font-weight:700; }
  /* 기간 모드 월별 차트 — 월 이름 / 막대 위 건수 */
  .axis-month { font-size:9.5px; fill:${COLORS.textMuted}; font-weight:600; }
  .bar-val { font-family:'Courier New',monospace; font-size:9.5px; font-weight:700; fill:${COLORS.navy}; pointer-events:none; }
  .bar-hit:hover { fill:rgba(10,31,51,.06); }

  /* Sankey */
  .sankey-wrap { overflow-x:auto; }
  .sankey-wrap svg { display:block; }
  /* 물결(링크) — hover 시 색을 진하게 하고 #chart-tooltip 에 출발 → 도착/건수를 띄운다 */
  .sk-link { fill:${COLORS.sankeyLink}; fill-opacity:.4; cursor:default; transition:fill .3s ease, fill-opacity .3s ease; }
  .sk-link:hover { fill:${COLORS.steelBlue}; fill-opacity:.65; transition-duration:.2s; }
  .sk-node:hover rect { stroke:${COLORS.navy}; stroke-width:1; }
  .sk-node.drillable:hover .sk-label { fill:${COLORS.link}; font-weight:700; text-decoration:underline; }
  .sk-label { font-size:10px; fill:#555; }
  .sk-head { font-size:10px; font-weight:700; fill:${COLORS.textMuted}; }
  .sk-badge-bg { fill:#EDF3F8; }
  .sk-badge { font-size:9px; font-weight:700; fill:${COLORS.steelBlue}; }
  /* Sankey hover 강조 — 원본 SVG 는 건드리지 않고, 위에 겹친 오버레이 SVG(.sk-over)에
     반투명 흰 막(.sk-veil) + 연결된 링크/노드 복제본만 그린다. 오버레이는 별도 레이어라 원본(수천 px 높이)을 다시 그리지 않는다 */
  .sk-stack { position:relative; }
  .sk-over { position:absolute; left:0; top:0; pointer-events:none; opacity:0; transition:opacity .3s ease; will-change:opacity; }
  .sk-over.on { opacity:1; transition-duration:.2s; }
  .sk-veil { fill:#fff; fill-opacity:.82; }
  .sk-over .sk-link { fill:${COLORS.steelBlue}; fill-opacity:.7; pointer-events:none; }
  .sk-over .sk-node.rel .sk-label, .sk-over .sk-node.cur .sk-label { fill:${COLORS.navy}; font-weight:700; }
  .sk-over .sk-node.cur rect { stroke:${COLORS.navy}; stroke-width:1; }
  .sk-over .sk-node.cur.drillable .sk-label { fill:${COLORS.link}; text-decoration:underline; }

  /* 링크 칩 */
  .op-links { font-size:9px; color:${COLORS.textMuted}; display:inline-flex; align-items:center; flex-wrap:wrap; gap:4px; }
  .op-links a, .op-links a.drill-link, .op-links a:link, .op-links a:visited {
    display:inline-block; padding:1px 6px;
    border:1px solid ${COLORS.link}; border-radius:4px;
    background:#fff; color:${COLORS.link} !important;
    font-family:'Courier New',monospace; font-size:8.5px; font-weight:700; line-height:1.5;
    text-decoration:underline; text-underline-offset:2px; cursor:pointer;
    transition:background .15s ease, color .15s ease, border-color .15s ease;
  }
  .op-links a:hover, .op-links a.drill-link:hover {
    background:${COLORS.linkHover}; color:#fff !important; border-color:${COLORS.linkHover}; text-decoration:none;
  }
  .op-links a * { color:inherit !important; text-decoration:inherit !important; }

  /* 표 */
  table.dash-table { width:100%; border-collapse:collapse; }
  table.dash-table thead { display:table-header-group; }
  table.dash-table th { background:${COLORS.navy}; color:#fff; font-size:8.3px; text-transform:uppercase; letter-spacing:.03em;
                        padding:5px 6px; text-align:right; font-weight:600; }
  table.dash-table th:first-child { text-align:left; }
  table.dash-table th.c, table.dash-table td.c { text-align:center; }
  table.dash-table th.l, table.dash-table td.l { text-align:left; }
  table.dash-table td { border-bottom:1px solid #EEF2F0; padding:4px 6px; text-align:right; vertical-align:middle;
                        font-family:'Courier New',monospace; font-size:9.3px; font-weight:700; }
  table.dash-table td:first-child { text-align:left; font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif;
                                    font-size:9px; font-weight:700; color:#101B26; }
  table.dash-table tr { page-break-inside:avoid; }
  table.dash-table tbody tr:nth-child(even) { background:${COLORS.rowAlt}; }
  tr.total-row td { background:${COLORS.totalBg}; color:${COLORS.navy}; font-weight:800; border-top:2px solid ${COLORS.gold}; border-bottom:none; }
  tr.total-row:nth-child(even) td { background:${COLORS.totalBg}; }
  .name-cell { max-width:260px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .name-cell.empty { color:${COLORS.faint}; font-style:italic; }
  .pos { color:${COLORS.up}; }
  .neg { color:${COLORS.down}; }
  .share-bar { display:inline-block; height:7px; background:${COLORS.steelBlue}; border-radius:2px; vertical-align:middle; margin-right:5px; opacity:.55; }
  .op-dot { display:inline-block; width:8px; height:8px; border-radius:2px; margin-right:4px; vertical-align:middle; }

  .note { margin-top:9px; background:#FFF9EC; border:1px solid #E8D6AC; border-left:3px solid ${COLORS.gold};
          border-radius:5px; padding:8px 11px; font-size:9px; color:${COLORS.textMuted}; line-height:1.6; }
  .note b { color:${COLORS.navy}; }
  .footnote { font-size:8.5px; color:${COLORS.faint}; margin-top:10px; line-height:1.6;
              border-top:1px solid ${COLORS.border}; padding-top:6px; display:flex; justify-content:space-between; }
  .page-break { break-before:page; page-break-before:always; margin-top:14px; }

  .chart-tooltip-box {
    position:fixed; display:none; z-index:9999;
    background:#fff; color:#101B26;
    font-family:'Courier New',monospace; font-size:9.5px; font-weight:700;
    padding:7px 10px; border-radius:5px; border:1px solid ${COLORS.border};
    pointer-events:none; line-height:1.7; white-space:nowrap;
    box-shadow:0 4px 14px rgba(14,31,51,.16);
  }
  .chart-tooltip-box .tip-month { color:${COLORS.navy}; font-family:-apple-system,sans-serif; font-size:9px; font-weight:800; display:block; margin-bottom:2px; }
  .chart-tooltip-box .tip-total { display:block; padding-bottom:3px; margin-bottom:2px; border-bottom:1px dashed ${COLORS.border}; }
  .chart-tooltip-box .tip-op { display:flex; align-items:center; gap:6px; }
  .chart-tooltip-box .tip-op .val { margin-left:auto; padding-left:10px; }
  .chart-tooltip-box .tip-click { display:block; margin-top:4px; font-size:9.5px; color:${COLORS.faint}; font-weight:800; font-family:-apple-system,sans-serif; }

  /* 차트 클릭 드릴다운 메뉴 — .dash-wrap 기준 absolute 라 스크롤을 따라간다 */
  .drillable { cursor:pointer; }
  .drill-menu { position:absolute; display:none; z-index:9999; min-width:230px; max-width:340px;
                background:#fff; border:1px solid ${COLORS.navy}; border-radius:8px; overflow:hidden;
                box-shadow:0 12px 32px rgba(10,31,51,.30), 0 0 0 3px rgba(184,134,46,.28);
                animation:bc-menu-in .12s ease-out; }
  @keyframes bc-menu-in { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:none; } }
  .drill-menu .drill-menu-title { background:${COLORS.navy}; color:#fff; padding:8px 12px 7px; font-size:11px; font-weight:700;
                line-height:1.3; word-break:break-word; }
  .drill-menu .drill-menu-title small { display:block; margin-bottom:2px; font-family:'Courier New',monospace; font-size:8px;
                letter-spacing:.12em; text-transform:uppercase; color:${COLORS.goldLight}; font-weight:700; }
  .drill-menu .op-links { display:flex; flex-direction:column; align-items:stretch; gap:0; padding:4px 0; }
  .drill-menu .op-links a, .drill-menu .op-links a:link, .drill-menu .op-links a:visited {
    display:flex; align-items:center; justify-content:flex-start; gap:8px; width:100%; margin:0; padding:8px 12px 8px 10px;
    border:none; border-left:3px solid transparent; border-radius:0; background:#fff; text-decoration:none; text-align:left;
    font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:11.5px; font-weight:600; color:#101B26 !important; }
  .drill-menu .op-links a::after { content:"\\2192"; margin-left:auto; padding-left:14px; color:${COLORS.faint}; font-size:12px; }
  .drill-menu .op-links a:hover { background:#EEF4FA; border-left-color:${COLORS.gold}; color:${COLORS.link} !important; }
  .drill-menu .op-links a:hover::after { color:${COLORS.link}; transform:translateX(2px); }
  .drill-menu .op-links a .op-dot { width:10px; height:10px; margin:0; flex-shrink:0; }
  .drill-boxes { display:none; }

  .state-wrap { min-height:70vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; }
  .spinner { width:40px; height:40px; border-radius:50%; border:4px solid #eee; border-top-color:${COLORS.gold}; animation:tsr-spin 0.8s linear infinite; }
  @keyframes tsr-spin { to { transform:rotate(360deg); } }
  .state-text { color:${COLORS.faint}; font-size:13px; }
  .state-text.error { color:${COLORS.down}; }

  @media print {
    .chart-tooltip-box { display:none !important; }
    .drill-menu { display:none !important; }
    .basis-switch { display:none !important; }
    .sk-over { display:none !important; }
    .op-links a, .kpi, .masthead, .insight, tr.total-row td, .note, .share-bar, .op-dot, .masthead .basis {
      -webkit-print-color-adjust:exact; print-color-adjust:exact;
    }
    .page { page-break-after:always; break-after:page; }
    .page:last-child { page-break-after:auto; break-after:auto; }
  }
`;

function fmtInt(n: number): string { return Math.round(n).toLocaleString("en-US"); }
function fmtPct(pct: number): string { return pct.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%"; }
/** 표시 자릿수(소수 1자리)로 반올림 — -0.03 → 0 (|| 0 으로 -0 도 0 으로).
 *  음수도 toLocaleString 처럼 0 에서 먼 쪽으로 반올림(-7.25 → -7.3) */
function roundPct(pct: number): number { return (Math.sign(pct) * Math.round(Math.abs(pct) * 10)) / 10 || 0; }
/** 반올림한 값으로 부호를 정해 "-0.0%" / "+-0.0%" 가 나오지 않게 한다 */
function fmtDelta(pct: number): string { const r = roundPct(pct); return (r >= 0 ? "+" : "") + fmtPct(r); }
/** 증감 색 — fmtDelta 와 같은 반올림 기준(0.0% 는 pos) */
function deltaCls(pct: number): string { return roundPct(pct) >= 0 ? "pos" : "neg"; }
/** "1 shipment" / "3 shipments" — 숫자는 fmtInt 로 */
function plural(n: number, one: string, many: string = one + "s"): string { return `${fmtInt(n)} ${Math.round(n) === 1 ? one : many}`; }
function escHtml(s: string): string {
  return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
/** 인라인 onclick 속성(작은따옴표로 감쌈) 안에 문자열을 안전하게 넣을 때 사용. */
const js = (value: string): string => JSON.stringify(value).replace(/'/g, "&#39;");
function truncate(s: string, max: number): string { return s.length <= max ? s : s.substring(0, max - 1) + "…"; }
function opVal(item: MonthlyData, key: "oi" | "oe" | "ai" | "ae" | "ot"): number { return Number(item[key] ?? 0); }
/** "2026-01-15" → 로컬 자정 Date (new Date("2026-01-15") 는 UTC 로 해석되므로 쓰지 않는다) */
function parseIsoDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? "").trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? new Date() : d;
}
function fmtMonthLabel(blDate: string): string {
  const [y, m] = blDate.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = Number(m) - 1;
  return names[idx] ? `${names[idx]} ${y}` : blDate;
}

function niceScale(maxValue: number, tickCount: number): { niceMax: number; step: number } {
  if (maxValue <= 0) return { niceMax: tickCount, step: 1 };
  const rough = maxValue / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = Math.max(1, factor * magnitude);
  return { niceMax: Math.ceil(maxValue / step) * step, step };
}

function drillArgs(opCode: string, asOfDate: string, customerId: number, nodeType: string, agentId: number, shipperName: string, blDate: string, baseType: string): string[] {
  return ["BusinessChartToListView", opCode, asOfDate, String(customerId), nodeType, String(agentId), shipperName, blDate, baseType];
}

/**
 * SVG 요소 클릭용 숨김 링크 상자. Operation이 하나면 그 링크를 바로 click(),
 * 여러 개면 #drill-menu 를 커서 위치에 띄워 고르게 한다(React 차트의 opMenu 와 같은 동작).
 */
function buildDrillBox(id: string, title: string, opCodes: string[], channelName: string, windowName: string, asOfDate: string, customerId: number, nodeType: string, agentId: number, shipperName: string, blDate: string, baseType: string): string {
  const links = OPS
    .filter(op => opCodes.includes(op.code))
    .map(op => buildBroadcastHyperlink(
      `<span class="op-dot" style="background:${op.color}"></span>${op.label}`,
      channelName,
      drillArgs(op.code, asOfDate, customerId, nodeType, agentId, shipperName, blDate, baseType),
      windowName))
    .join("");
  return `<div id="${id}"><div class="drill-menu-title"><small>Open shipment list</small>${escHtml(title)}</div><span class="op-links">${links}</span></div>`;
}

function drillClickAttr(id: string): string {
  return `onclick='(function(e,id){
    var box=document.getElementById(id); if(!box) return;
    var links=box.querySelectorAll("a");
    if(links.length===0) return;
    if(links.length===1){ links[0].click(); return; }
    var m=document.getElementById("drill-menu"); if(!m) return;
    m.innerHTML=box.innerHTML;
    m.style.display="block";
    var r=(m.offsetParent||document.body).getBoundingClientRect();
    var left=Math.min(e.clientX+8, window.innerWidth-m.offsetWidth-16);
    var top=e.clientY+8;
    if(top+m.offsetHeight>window.innerHeight-8) top=e.clientY-m.offsetHeight-8;
    m.style.left=(left-r.left)+"px";
    m.style.top=(top-r.top)+"px";
    e.stopPropagation();
  })(event,${js(id)})'`;
}

/**
 * Sankey hover — 원본 svg 하나에서 이벤트 위임으로 처리한다.
 * [노드 hover] 강조 표시는 오버레이 svg(.sk-over)에만 그린다.
 * - 인접 링크/노드 목록은 첫 hover 때 한 번만 만들어 svg.__sk 에 캐시
 * - 원본 svg 의 클래스·스타일은 바꾸지 않는다 → 원본 전체 스타일 재계산/재페인트가 없다
 * - 오버레이: 흰 막(.sk-veil) 위에 연결 링크·노드 복제본만 넣는다(연결 수만큼만 DOM 작업)
 * - 오버레이는 .on 클래스로 opacity 를 서서히 켜고(.2s) 끈다(.3s). 끌 때는 내용을 지우지 않아 사라지는 동안에도 보인다
 * - 물결 자체 hover 색도 CSS transition 으로 서서히 진해지고(.2s) 밝아진다(.3s)
 * - 강조 반영은 프레임당 한 번(rAF), 해제는 노드에서 나가는 즉시
 * [물결(링크) hover] #chart-tooltip 에 출발 → 도착 업체, 건수, Operation 별 건수(data-o)를 보여준다.
 * [노드 hover] 막대·회사명 모두 물결과 같은 형태의 tooltip(이름, 건수, Operation 별 건수, 클릭 안내)을 띄운다.
 * [겹침 방지] 막대 tooltip 과 물결 tooltip 은 같은 #chart-tooltip 하나를 쓴다.
 * - 노드에 들어가면 물결 tooltip 을 바로 닫는다
 * - 물결에 들어가면 노드 강조를 즉시 닫고 물결 tooltip 을 띄운다
 */
function sankeyHoverAttrs(): string {
  // 링크 tooltip 용 Operation 라벨/색 — {"OI":["Ocean Import","#3B82F6"], …} (작은따옴표 없음)
  const opMeta = "{" + OPS.map(op => `${JSON.stringify(op.code)}:[${JSON.stringify(op.label)},${JSON.stringify(op.color)}]`).join(",") + "}";
  const init = `var st=svg.__sk;
    if(!st){
      var over=svg.parentNode.querySelector(".sk-over");
      st=svg.__sk={adj:{},nodes:{},cur:null,want:null,raf:0,tipOn:false,
        over:over, hl:over?over.querySelector(".sk-hl"):null};
      svg.querySelectorAll(".sk-node").forEach(function(x){ st.nodes[x.getAttribute("data-n")]=x; });
      svg.querySelectorAll(".sk-link").forEach(function(p){
        var s=p.getAttribute("data-s"), t=p.getAttribute("data-t");
        (st.adj[s]=st.adj[s]||[]).push([p,t,"out"]);
        (st.adj[t]=st.adj[t]||[]).push([p,s,"in"]);
      });
      st.tip=document.getElementById("chart-tooltip");
      st.esc=function(x){ return String(x==null?"":x).replace(/\\u0026/g,"\\u0026amp;").replace(/</g,"\\u0026lt;").replace(/>/g,"\\u0026gt;"); };
      st.num=function(x){ return Math.round(Number(x||0)).toLocaleString("en-US"); };
      st.ship=function(x){ return st.num(x)+(Number(x)===1?" Shipment":" Shipments"); };
      st.placeTip=function(){
        var t=st.tip; if(!t||!st.tipOn) return;
        var x=st.mx+14, y=st.my+14;
        if(x+t.offsetWidth>window.innerWidth-8) x=Math.max(8,st.mx-14-t.offsetWidth);
        if(y+t.offsetHeight>window.innerHeight-8) y=Math.max(8,st.my-14-t.offsetHeight);
        t.style.left=x+"px"; t.style.top=y+"px";
      };
      var OPM=${opMeta};
      st.opRows=function(o){
        var html="";
        (o||"").split(",").forEach(function(p){
          if(!p) return;
          var kv=p.split(":"), m=OPM[kv[0]]||[kv[0],"${COLORS.faint}"];
          html+="<span class=tip-op><span class=op-dot style=background:"+m[1]+"></span>"+st.esc(m[0])+"<span class=val>"+st.num(kv[1])+"</span></span>";
        });
        return html;
      };
      st.showTip=function(lk){
        var t=st.tip; if(!t) return;
        var sn=st.nodes[lk.getAttribute("data-s")], tn=st.nodes[lk.getAttribute("data-t")];
        var html="<span class=tip-month>"+st.esc(sn?sn.getAttribute("data-name"):"")+"<br>→ "+st.esc(tn?tn.getAttribute("data-name"):"")+"</span>"
          +"<span class=tip-total>"+st.ship(lk.getAttribute("data-v"))+"</span>"
          +st.opRows(lk.getAttribute("data-o"));
        t.innerHTML=html; t.style.display="block"; st.tipOn=true; st.placeTip();
      };
      /* 막대(노드 사각형) hover — 물결과 같은 형태의 tooltip */
      st.showNodeTip=function(g){
        var t=st.tip; if(!t) return;
        var html="<span class=tip-month>"+st.esc(g.getAttribute("data-name"))+"</span>"
          +"<span class=tip-total>"+st.ship(g.getAttribute("data-v"))+"</span>"
          +st.opRows(g.getAttribute("data-o"))
          +(g.classList.contains("drillable")?"<span class=tip-click>Click to open the shipment list</span>":"");
        t.innerHTML=html; t.style.display="block"; st.tipOn=true; st.placeTip();
      };
      /* 강조 중인 노드의 tooltip — 노드면 띄우고, 강조 해제면 닫는다 */
      st.info=function(g){
        if(g) st.showNodeTip(g); else st.hideTip();
      };
      st.hideTip=function(){ if(st.tipOn&&st.tip) st.tip.style.display="none"; st.tipOn=false; };
      st.apply=function(){
        st.raf=0;
        if(st.want===st.cur||!st.hl||!st.over) return;
        st.cur=st.want;
        // 해제 — 내용은 그대로 둔 채 오버레이만 서서히 투명하게(.sk-over 의 opacity transition)
        if(st.cur===null){ st.over.classList.remove("on"); st.info(null); return; }
        while(st.hl.firstChild) st.hl.removeChild(st.hl.firstChild);
        var frag=document.createDocumentFragment(), marks=[];
        var g=st.nodes[st.cur];
        var list=st.adj[st.cur]||[];
        for(var j=0;j<list.length;j++){
          frag.appendChild(list[j][0].cloneNode(false));
          var n=st.nodes[list[j][1]]; if(n&&n!==g) marks.push([n,"rel"]);
        }
        if(g) marks.push([g,"cur"]);
        for(var k=0;k<marks.length;k++){
          var c=marks[k][0].cloneNode(true);
          c.removeAttribute("onclick"); c.classList.add(marks[k][1]);
          frag.appendChild(c);
        }
        st.hl.appendChild(frag);
        st.over.classList.add("on");
        st.info(g);
      };
    }`;
  return `onmouseover='(function(e,svg){
    if(!e.target.closest) return;
    var lk=e.target.closest(".sk-link"), g=lk?null:e.target.closest(".sk-node");
    if(!lk&&!g) return;
    ${init}
    st.mx=e.clientX; st.my=e.clientY;
    if(lk){
      if(st.raf){ cancelAnimationFrame(st.raf); st.raf=0; }
      st.want=null; st.apply();
      st.showTip(lk);
      return;
    }
    var n=g.getAttribute("data-n");
    // 이미 강조 중인 노드(막대 ⇄ 회사명 이동) — 강조는 그대로, tooltip 만 다시 띄운다
    if(st.cur===n&&st.want===n){ st.info(g); return; }
    st.want=n;
    if(!st.raf) st.raf=requestAnimationFrame(st.apply);
  })(event,this)'
  onmousemove='(function(e,svg){
    var st=svg.__sk; if(!st) return;
    st.mx=e.clientX; st.my=e.clientY; st.placeTip();
  })(event,this)'
  onmouseout='(function(e,svg){
    var st=svg.__sk; if(!st) return;
    var to=e.relatedTarget;
    if(e.target!==to&&e.target.classList&&e.target.classList.contains("sk-link")) st.hideTip();
    // 노드(막대·회사명)에서 노드 밖으로 나가면 tooltip 과 강조를 바로 해제한다
    var from=e.target.closest?e.target.closest(".sk-node"):null;
    var toNode=to&&to.closest?to.closest(".sk-node"):null;
    if(from&&toNode!==from) st.hideTip();
    if(toNode||!from) return;
    if(st.raf){ cancelAnimationFrame(st.raf); st.raf=0; }
    st.want=null; st.apply();
  })(event,this)'`;
}

// ============================================================
// Monthly stacked bar chart
// ============================================================

/**
 * @param periodMode 기간 모드(Annual / Monthly·Quarterly 리포트에서 열림, 최대 13개월).
 *   막대가 적으므로 패널 폭 전체에 펼치고, 막대 사이 간격·월 이름·막대 위 건수를 그린다.
 *   5년(60개월)은 기존 모양 그대로.
 */
function buildMonthlyChartSvg(data: MonthlyData[], drillIds: (string | null)[], periodMode: boolean = false): string {
  const n = data.length;
  const axisW = 46;
  const padTop = periodMode ? 22 : 14;        // 기간 모드: 막대 위 건수 자리
  const padBottom = periodMode ? 46 : 40;     // 기간 모드: 월 이름 + 연도 두 줄
  const padRight = 12;
  const height = periodMode ? 270 : 250;
  const chartH = height - padTop - padBottom;
  const fullW = 1080 - axisW - padRight;

  const colW = periodMode
    ? Math.max(10, Math.floor(fullW / Math.max(n, 1)))
    : Math.max(10, Math.min(22, Math.floor(fullW / Math.max(n, 1))));
  const barW = periodMode
    ? Math.min(52, Math.max(8, Math.round(colW * 0.56)))
    : Math.max(6, colW - 4);
  const width = axisW + n * colW + padRight;

  const breakdown = data.some(d => OPS.some(op => opVal(d, op.key) > 0));
  const max = Math.max(...data.map(d => Number(d.volume) || 0), 0);
  const { niceMax, step } = niceScale(max, 5);
  const yOf = (v: number) => padTop + chartH - (niceMax > 0 ? (v / niceMax) * chartH : 0);

  // 연도 밴드
  const bands: { year: string; start: number; count: number }[] = [];
  data.forEach((d, i) => {
    const year = (d.blDate ?? "").split("-")[0] ?? "";
    const last = bands[bands.length - 1];
    if (last && last.year === year) last.count += 1;
    else bands.push({ year, start: i, count: 1 });
  });

  let bandHtml = "";
  bands.forEach((b, i) => {
    const x = axisW + b.start * colW;
    if (i % 2 === 1) {
      bandHtml += `<rect x="${x}" y="${padTop}" width="${b.count * colW}" height="${chartH}" fill="${COLORS.yearBand}"></rect>`;
    }
    if (i > 0) {
      bandHtml += `<line x1="${x}" y1="${padTop}" x2="${x}" y2="${padTop + chartH + (periodMode ? 38 : 6)}" stroke="${COLORS.border}"></line>`;
    }
    bandHtml += `<text x="${x + (b.count * colW) / 2}" y="${padTop + chartH + (periodMode ? 36 : 24)}" text-anchor="middle" class="axis-year">${b.year}</text>`;
  });

  // 기간 모드: 막대마다 월 이름(Jan, Feb …)
  const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthLabelHtml = periodMode
    ? data.map((d, i) => {
      const m = Number((d.blDate ?? "").split("-")[1]) - 1;
      return `<text x="${axisW + i * colW + colW / 2}" y="${padTop + chartH + 16}" text-anchor="middle" class="axis-month">${MONTH_ABBR[m] ?? ""}</text>`;
    }).join("")
    : "";

  // 기간 모드: 막대 위 월 합계
  const valueLabelHtml = periodMode
    ? data.map((d, i) => {
      const total = Number(d.volume) || 0;
      if (total <= 0) return "";
      return `<text x="${axisW + i * colW + colW / 2}" y="${(yOf(total) - 5).toFixed(1)}" text-anchor="middle" class="bar-val">${fmtInt(total)}</text>`;
    }).join("")
    : "";

  let gridHtml = "";
  for (let v = 0; v <= niceMax + step / 2; v += step) {
    const y = yOf(v);
    const base = v === 0;
    gridHtml += `
      <line x1="${axisW}" y1="${y.toFixed(1)}" x2="${width - padRight}" y2="${y.toFixed(1)}"
        stroke="${base ? "#9CA3AF" : COLORS.grid}" ${base ? "" : 'stroke-dasharray="3,3"'}></line>
      <text x="${axisW - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end" class="axis">${fmtInt(v)}</text>`;
  }

  const barsHtml = data.map((d, i) => {
    const x = axisW + i * colW + (colW - barW) / 2;
    const total = Number(d.volume) || 0;
    if (total <= 0) return "";
    if (!breakdown) {
      const h = (total / niceMax) * chartH;
      return `<rect x="${x}" y="${(padTop + chartH - h).toFixed(1)}" width="${barW}" height="${h.toFixed(1)}" fill="${OPS[0].color}" rx="1"></rect>`;
    }
    let acc = 0;
    return OPS.map(op => {
      const v = opVal(d, op.key);
      if (v <= 0) return "";
      const h = (v / niceMax) * chartH;
      const y = yOf(acc) - h;
      acc += v;
      return `<rect x="${x}" y="${y.toFixed(1)}" width="${barW}" height="${h.toFixed(1)}" fill="${op.color}"></rect>`;
    }).join("");
  }).join("");

  // hover hit-zone — 5Year 차트와 같이 #chart-tooltip 을 직접 움직여 즉시 반응
  const hitHtml = data.map((d, i) => {
    const x = axisW + i * colW;
    const total = Number(d.volume) || 0;
    const drillId = drillIds[i];
    const cls = drillId ? "bar-hit drillable" : "bar-hit";
    const click = drillId ? drillClickAttr(drillId) : "";

    const lines = [
      `<span class="tip-month">${escHtml(fmtMonthLabel(d.blDate))}</span>`,
      `<span class="tip-total">${total === 1 ? "1 Shipment" : `${fmtInt(total)} Shipments`}</span>`,
    ];
    if (breakdown) {
      OPS.forEach(op => {
        const v = opVal(d, op.key);
        if (v > 0) lines.push(`<span class="tip-op"><span class="op-dot" style="background:${op.color}"></span>${op.label}<span class="val">${fmtInt(v)}</span></span>`);
      });
    }
    if (drillId) lines.push(`<span class="tip-click">Click to open the shipment list</span>`);
    const content = js(lines.join(""));

    return `<rect class="${cls}" x="${x}" y="${padTop}" width="${colW}" height="${chartH}" fill="transparent" ${click}
      onmousemove='(function(e){
        var t=document.getElementById("chart-tooltip"); if(!t) return;
        t.innerHTML=${content};
        t.style.display="block";
        var left=e.clientX+14; if(left+220>window.innerWidth) left=e.clientX-14-t.offsetWidth;
        t.style.left=left+"px";
        t.style.top=(e.clientY+14)+"px";
      })(event)'
      onmouseout='(function(){ var t=document.getElementById("chart-tooltip"); if(t) t.style.display="none"; })()'
    ></rect>`;
  }).join("");

  return `
    <svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Monthly shipment volume">
      ${bandHtml}
      ${gridHtml}
      ${barsHtml}
      ${monthLabelHtml}
      ${valueLabelHtml}
      ${hitHtml}
    </svg>`;
}

// ============================================================
// Sankey — 정적 SVG
//   Customer 기준: Customer(0) → Agent(1) → Shipper(2)
//   Agent 기준:    Agent(0) → Customer(1) → Shipper(2)
//   가운데 레이어는 상위 SANKEY_MAX_MIDDLE 개, 가운데 노드마다 Shipper 는 상위 SANKEY_MAX_SHIPPERS_PER_MIDDLE 개까지만
// ============================================================

interface SkNode { id: string; name: string; layer: 0 | 1 | 2; value: number; x: number; y: number; h: number; color: string; customerId: number; agentId: number; ops: Set<string>; }
interface SkLink { source: string; target: string; value: number; sy: number; ty: number; sh: number; th: number; ops: Map<string, number>; }

function buildSankeySvg(transactions: Transaction[], width: number, baseType: string, drillIdFor: (node: SkNode) => string | null): string {
  const isAgentBase = baseType === "AGENT";
  const NODE_W = 10;
  const NODE_PAD = 8;
  const NODE_MIN_H = 3;
  const LINK_MIN_H = 3;
  const PAD = 20;
  const HEADER_H = 34;
  const TEXT_GAP = 8;
  const MAX_LABEL = 38;
  const MIN_TEXT_GAP = 14;
  const FILL = [0.7, 0.85, 0.92];
  const XPOS = [0, 0.36, 0.72];

  // 가운데 레이어(상대 업체) 물량 상위 N개, 그 노드마다 Shipper 물량 상위 M개만 남긴다.
  // 왼쪽 → 가운데 링크는 남긴 가운데 노드의 전체 물량, 가운데 → 오른쪽 링크는 남긴 Shipper 분만 그린다.
  const middleOf = (t: Transaction) => (isAgentBase ? t.customerName : t.agentName);
  const middleVol = new Map<string, number>();
  const middleShipperVol = new Map<string, Map<string, number>>();
  transactions.forEach(t => {
    const m = middleOf(t);
    middleVol.set(m, (middleVol.get(m) ?? 0) + t.volume);
    const sv = middleShipperVol.get(m) ?? new Map<string, number>();
    sv.set(t.shipperName, (sv.get(t.shipperName) ?? 0) + t.volume);
    middleShipperVol.set(m, sv);
  });
  const topMiddles = new Set(
    Array.from(middleVol.entries()).sort((a, b) => b[1] - a[1]).slice(0, SANKEY_MAX_MIDDLE).map(e => e[0]));
  const keptShippers = new Map<string, Set<string>>();
  topMiddles.forEach(m => {
    keptShippers.set(m, new Set(
      Array.from(middleShipperVol.get(m)!.entries()).sort((a, b) => b[1] - a[1]).slice(0, SANKEY_MAX_SHIPPERS_PER_MIDDLE).map(e => e[0])));
  });

  const customers = new Map<string, SkNode>();
  const agents = new Map<string, SkNode>();
  const shippers = new Map<string, SkNode>();
  const linkMap = new Map<string, SkLink>();

  const ensure = (map: Map<string, SkNode>, id: string, name: string, layer: 0 | 1 | 2, customerId: number, agentId: number, op?: string) => {
    if (!map.has(id)) map.set(id, { id, name, layer, value: 0, x: 0, y: 0, h: 0, color: "", customerId, agentId, ops: new Set<string>() });
    if (op) map.get(id)!.ops.add(op);
  };
  const ensureLink = (source: string, target: string, v: number, op: string | undefined) => {
    const key = `${source}->${target}`;
    if (!linkMap.has(key)) linkMap.set(key, { source, target, value: 0, sy: 0, ty: 0, sh: 0, th: 0, ops: new Map<string, number>() });
    const l = linkMap.get(key)!;
    l.value += v;
    if (op) l.ops.set(op, (l.ops.get(op) ?? 0) + v);
  };

  transactions.forEach(t => {
    const mid = middleOf(t);
    if (!topMiddles.has(mid)) return;
    const c = `c-${t.customerName}`, a = `a-${t.agentName}`, s = `s-${t.shipperName}`;
    ensure(customers, c, t.customerName, isAgentBase ? 1 : 0, t.customerName === EMPTY_CUSTOMER_LABEL ? 0 : (t.customerId ?? 0), 0, t.operation);
    ensure(agents, a, t.agentName, isAgentBase ? 0 : 1, 0, t.agentName === EMPTY_AGENT_LABEL ? 0 : (t.agentId ?? 0), t.operation);
    if (isAgentBase) ensureLink(a, c, t.volume, t.operation);
    else ensureLink(c, a, t.volume, t.operation);

    if (!keptShippers.get(mid)!.has(t.shipperName)) return;
    ensure(shippers, s, t.shipperName, 2, 0, 0, t.operation);
    if (isAgentBase) ensureLink(c, s, t.volume, t.operation);
    else ensureLink(a, s, t.volume, t.operation);
  });

  const links = Array.from(linkMap.values());
  const layers: SkNode[][] = isAgentBase
    ? [Array.from(agents.values()), Array.from(customers.values()), Array.from(shippers.values())]
    : [Array.from(customers.values()), Array.from(agents.values()), Array.from(shippers.values())];
  const nodes = layers.flat();
  const byId = new Map(nodes.map(n => [n.id, n]));
  // hover 강조용 노드 번호 (링크의 data-s/data-t, 노드의 data-n)
  const nodeIndex = new Map(nodes.map((n, i) => [n.id, i]));

  // 막대 tooltip 용 노드별 Operation 건수 — "OI:12,OE:3" (노드 값을 만든 쪽 링크 기준)
  const nodeOps = new Map<string, string>();
  nodes.forEach(n => {
    const ins = links.filter(l => l.target === n.id);
    const outs = links.filter(l => l.source === n.id);
    const inV = ins.reduce((s, l) => s + l.value, 0);
    const outV = outs.reduce((s, l) => s + l.value, 0);
    n.value = n.layer === 0 ? outV : n.layer === 2 ? inV : Math.max(inV, outV);

    const used = n.layer === 0 ? outs : n.layer === 2 ? ins : (inV >= outV ? ins : outs);
    const opSum = new Map<string, number>();
    used.forEach(l => l.ops.forEach((v, k) => opSum.set(k, (opSum.get(k) ?? 0) + v)));
    nodeOps.set(n.id, OPS.filter(op => opSum.has(op.code)).map(op => `${op.code}:${opSum.get(op.code)}`).join(","));
  });

  const minH = (shippers.size * MIN_TEXT_GAP) / FILL[2] + PAD * 2;
  const height = Math.max(520, minH);
  const availH = height - PAD * 2;
  const usableW = width - NODE_W;
  const layerX = XPOS.map(p => usableW * p);

  const scales = layers.map((layer, i) => {
    const total = layer.reduce((s, n) => s + n.value, 0);
    const usable = availH * FILL[i] - (layer.length - 1) * NODE_PAD;
    return total > 0 ? usable / total : 1;
  });

  layers.forEach((layer, i) => {
    layer.sort((a, b) => b.value - a.value);
    const hs = layer.map(n => Math.max(n.value * scales[i], NODE_MIN_H));
    const totalH = hs.reduce((s, h) => s + h, 0) + (layer.length - 1) * NODE_PAD;
    let y = PAD + (availH - totalH) / 2;
    layer.forEach((n, idx) => {
      n.x = layerX[i]; n.y = y; n.h = hs[idx];
      n.color = layer.length === 1 ? SANKEY_PALETTE[0]
        : SANKEY_PALETTE[Math.floor((idx / (layer.length - 1)) * (SANKEY_PALETTE.length - 1))];
      y += n.h + NODE_PAD;
    });
  });

  // 링크 두께: 노드 높이를 넘지 않도록 스케일
  const sSum = new Map<string, number>(), tSum = new Map<string, number>();
  links.forEach(l => {
    const sN = byId.get(l.source)!, tN = byId.get(l.target)!;
    l.sh = Math.max(l.value * scales[sN.layer], LINK_MIN_H);
    l.th = Math.max(l.value * scales[tN.layer], LINK_MIN_H);
    sSum.set(l.source, (sSum.get(l.source) ?? 0) + l.sh);
    tSum.set(l.target, (tSum.get(l.target) ?? 0) + l.th);
  });
  links.forEach(l => {
    const sN = byId.get(l.source)!, tN = byId.get(l.target)!;
    const sf = (sSum.get(l.source) ?? 0) > sN.h ? sN.h / sSum.get(l.source)! : 1;
    const tf = (tSum.get(l.target) ?? 0) > tN.h ? tN.h / tSum.get(l.target)! : 1;
    l.sh *= sf; l.th *= tf;
  });

  links.sort((a, b) => (byId.get(a.target)?.y ?? 0) - (byId.get(b.target)?.y ?? 0));
  const sOff = new Map<string, number>(), tOff = new Map<string, number>();
  links.forEach(l => {
    const sN = byId.get(l.source)!, tN = byId.get(l.target)!;
    l.sy = sN.y + (sOff.get(l.source) ?? 0);
    l.ty = tN.y + (tOff.get(l.target) ?? 0);
    sOff.set(l.source, (sOff.get(l.source) ?? 0) + l.sh);
    tOff.set(l.target, (tOff.get(l.target) ?? 0) + l.th);
  });

  const linkHtml = links.map(l => {
    const sN = byId.get(l.source)!, tN = byId.get(l.target)!;
    const sx = sN.x + NODE_W, tx = tN.x;
    const xi = (tx - sx) * 0.4;
    const d = `M ${sx} ${l.sy} C ${sx + xi} ${l.sy}, ${tx - xi} ${l.ty}, ${tx} ${l.ty}
               L ${tx} ${l.ty + l.th} C ${tx - xi} ${l.ty + l.th}, ${sx + xi} ${l.sy + l.sh}, ${sx} ${l.sy + l.sh} Z`;
    // tooltip 용 Operation 별 건수 — "OI:12,OE:3" (OPS 순서)
    const opsAttr = OPS.filter(op => l.ops.has(op.code)).map(op => `${op.code}:${l.ops.get(op.code)}`).join(",");
    return `<path class="sk-link" data-s="${nodeIndex.get(l.source)}" data-t="${nodeIndex.get(l.target)}" data-v="${l.value}" data-o="${opsAttr}" d="${d}"></path>`;
  }).join("");

  const layerLabels = isAgentBase ? ["Agent", "Customer", "Shipper"] : ["Customer", "Agent", "Shipper"];
  const nodeHtml = nodes.map((n, i) => {
    const drillId = drillIdFor(n);
    const cls = drillId ? "sk-node drillable" : "sk-node";
    const click = drillId ? drillClickAttr(drillId) : "";
    return `
    <g class="${cls}" data-n="${i}" data-name="${escHtml(n.name)}" data-v="${n.value}" data-o="${nodeOps.get(n.id) ?? ""}" data-k="${layerLabels[n.layer]}" ${click}>
      <rect x="${n.x}" y="${n.y.toFixed(1)}" width="${NODE_W}" height="${n.h.toFixed(1)}" fill="${n.color}" rx="1"></rect>
      <text x="${n.x + NODE_W + TEXT_GAP}" y="${(n.y + n.h / 2).toFixed(1)}" dominant-baseline="middle" class="sk-label">${escHtml(truncate(n.name, MAX_LABEL))}</text>
    </g>`;
  }).join("");

  const customerHeader = { label: "Customer", count: customers.size, w: 54 };
  const agentHeader = { label: "Agent", count: agents.size, w: 36 };
  const shipperHeader = { label: "Shipper", count: shippers.size, w: 44 };
  const headers = isAgentBase
    ? [agentHeader, customerHeader, shipperHeader]
    : [customerHeader, agentHeader, shipperHeader];
  const headerHtml = headers.map((h, i) => {
    const lx = layerX[i] + NODE_W + TEXT_GAP;
    const txt = fmtInt(h.count);
    const bw = Math.max(20, txt.length * 6.5 + 12);
    const bx = lx + h.w + 7;
    return `
      <text x="${lx}" y="16" dominant-baseline="middle" class="sk-head">${h.label}</text>
      <rect x="${bx}" y="8" width="${bw}" height="16" rx="8" class="sk-badge-bg"></rect>
      <text x="${bx + bw / 2}" y="16" text-anchor="middle" dominant-baseline="middle" class="sk-badge">${txt}</text>`;
  }).join("");

  return `
    <div class="sk-stack" style="width:${width}px;height:${height + HEADER_H}px">
      <svg viewBox="0 0 ${width} ${height + HEADER_H}" width="${width}" height="${height + HEADER_H}" role="img" aria-label="${headers.map(h => h.label).join(" / ")} flow" ${sankeyHoverAttrs()}>
        ${headerHtml}
        <g transform="translate(0, ${HEADER_H})">
          ${linkHtml}
          ${nodeHtml}
        </g>
      </svg>
      <svg class="sk-over" viewBox="0 0 ${width} ${height + HEADER_H}" width="${width}" height="${height + HEADER_H}" aria-hidden="true">
        <rect class="sk-veil" x="0" y="${HEADER_H}" width="${width}" height="${height}"></rect>
        <g class="sk-hl" transform="translate(0, ${HEADER_H})"></g>
      </svg>
    </div>`;
}

// ============================================================
// Report body
// ============================================================

function buildReportInnerHtml(
  sankeyData: Transaction[],
  monthlyData: MonthlyData[],
  asOfDate: Date,
  asOfIso: string,
  companyName: string,
  baseName: string,
  baseId: number,
  baseType: string,
  department: string,
  channelName: string,
  windowName: string,
  sourceText: string,
  notice: string = "",
  canSwitch: boolean = false,
  fromIso: string = "",
): string {
  if (sankeyData.length === 0 && !monthlyData.some(d => Number(d.volume) > 0)) {
    return `<div class="dash-wrap"><p style="padding:24px;color:${COLORS.faint};">No Data Found.</p></div>`;
  }

  // 기간 모드(Monthly / Quarterly 리포트에서 열림) — fromIso ~ asOfIso. 아니면 기존 5년.
  const isPeriod = !!fromIso;
  const fromDateObj = isPeriod ? parseIsoDate(fromIso) : null;
  const fromStr = fromDateObj ? fmtAsOf(fromDateObj) : "";
  // 드릴다운·View as 링크의 날짜 자리 — 기간 모드면 "from~to" 로 넘겨 다음 화면도 같은 기간을 쓴다
  const dateArg = isPeriod ? `${fromIso}~${asOfIso}` : asOfIso;

  // 기준 업체(baseType)가 Customer 면 상대는 Agent, 기준이 Agent 면 상대는 Customer
  const isAgentBase = baseType === "AGENT";
  const baseLabel = isAgentBase ? "Agent" : "Customer";
  const partnerLabel = isAgentBase ? "Customer" : "Agent";
  const partnerLower = partnerLabel.toLowerCase();
  const emptyPartnerLabel = isAgentBase ? EMPTY_CUSTOMER_LABEL : EMPTY_AGENT_LABEL;
  const baseCustomerId = isAgentBase ? 0 : baseId;
  const baseAgentId = isAgentBase ? baseId : 0;

  const monthly = [...monthlyData]
    .filter(d => !!d.blDate)
    .sort((a, b) => a.blDate.localeCompare(b.blDate));
  const breakdown = monthly.some(d => OPS.some(op => opVal(d, op.key) > 0));
  // 호출부가 new Date("YYYY-MM-DD")(UTC 자정)로 넘기면 UTC 보다 느린 시간대(미국 등)에서 하루 전으로 보이고,
  // 1월 1일 기준이면 연도까지 바뀐다. 날짜 문자열이 있으면 로컬 날짜로 다시 읽는다.
  const asOfLocal = /^\d{4}-\d{2}-\d{2}/.test(asOfIso ?? "") ? parseIsoDate(asOfIso) : asOfDate;
  const baseYear = asOfLocal.getFullYear();
  const asOfMonth = asOfLocal.getMonth() + 1;
  const asOfStr = fmtAsOf(asOfLocal);
  // 기준일이 12월 31일이면 그해는 완료된 연도 — "YTD" 를 붙이지 않는다
  const isYearEnd = asOfLocal.getMonth() === 11 && asOfLocal.getDate() === 31;
  const ytdTag = isYearEnd ? "" : " YTD";
  const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // ---------- 연도 집계 ----------
  const years = Array.from(new Set(monthly.map(d => d.blDate.split("-")[0]))).sort();
  const yearRows = years.map(y => {
    const rows = monthly.filter(d => d.blDate.startsWith(y));
    const total = rows.reduce((s, d) => s + (Number(d.volume) || 0), 0);
    const byOp = OPS.map(op => rows.reduce((s, d) => s + opVal(d, op.key), 0));
    const sameToDate = rows
      .filter(d => Number(d.blDate.split("-")[1]) <= asOfMonth)
      .reduce((s, d) => s + (Number(d.volume) || 0), 0);
    const firstMonth = rows.length ? Number(rows[0].blDate.split("-")[1]) : 1;
    return { year: y, total, byOp, sameToDate, months: rows.length, firstMonth };
  });
  const fiveYearTotal = yearRows.reduce((s, r) => s + r.total, 0);
  // 5년 창의 첫 해는 기준일 5년 전 달부터라 일부 달만 있다(예: 2021 은 Oct~Dec) — 표에 표시하고 YoY·최고 연도 비교에서 뺀다
  const isPartialFirstYear = (i: number) => !isPeriod && i === 0 && yearRows.length > 1 && yearRows[0].firstMonth > 1;
  const yearLabel = (r: { year: string; firstMonth: number }, i: number) =>
    r.year === String(baseYear) ? `${r.year}${ytdTag}`
      : isPartialFirstYear(i) ? `${r.year} (${r.firstMonth === 12 ? "Dec" : `${MONTH_SHORT[r.firstMonth - 1]}&ndash;Dec`})`
        : r.year;

  const ytdRow = yearRows.find(r => r.year === String(baseYear));
  const priorRow = yearRows.find(r => r.year === String(baseYear - 1));
  const ytdVolume = ytdRow?.total ?? 0;
  const priorSamePeriod = priorRow?.sameToDate ?? 0;
  const ytdDeltaPct = priorSamePeriod ? ((ytdVolume - priorSamePeriod) / priorSamePeriod) * 100 : 0;

  const peakMonth = monthly.reduce((m, d) => ((Number(d.volume) || 0) > (Number(m.volume) || 0) ? d : m), monthly[0] ?? { blDate: "", volume: 0 });
  const peakVolume = Number(peakMonth.volume) || 0;
  // 최고 연도 — 일부 달만 있는 첫 해는 빼고, 실적이 있는 연도가 2개 이상일 때만 의미가 있다
  const comparableYears = yearRows.filter((r, i) => !isPartialFirstYear(i) && r.total > 0);
  const peakYearMax = Math.max(0, ...comparableYears.map(r => r.total));
  // 1위가 하나일 때만 (모든 해가 같으면 "가장 많은 해"라는 말이 의미 없다)
  const peakYear = comparableYears.length >= 2 && comparableYears.filter(r => r.total === peakYearMax).length === 1
    ? comparableYears.find(r => r.total === peakYearMax)!
    : null;

  // ---------- Operation mix (5년) ----------
  const opTotals = OPS.map(op => ({ op, total: monthly.reduce((s, d) => s + opVal(d, op.key), 0) }))
    .filter(x => x.total > 0);
  const topOp = opTotals.reduce((a, b) => (b.total > a.total ? b : a), opTotals[0] ?? { op: OPS[0], total: 0 });
  const topOpMix = fiveYearTotal ? (topOp.total / fiveYearTotal) * 100 : 0;

  // ---------- Partner(Agent 또는 Customer) / Shipper 집계 ----------
  const partnerMap = new Map<string, { name: string; volume: number; ops: Set<string>; shippers: Set<string> }>();
  const shipperMap = new Map<string, { name: string; volume: number; ops: Set<string>; partners: Set<string> }>();
  sankeyData.forEach(t => {
    const partnerName = isAgentBase ? t.customerName : t.agentName;
    const p = partnerMap.get(partnerName) ?? { name: partnerName, volume: 0, ops: new Set<string>(), shippers: new Set<string>() };
    p.volume += t.volume; if (t.operation) p.ops.add(t.operation); p.shippers.add(t.shipperName);
    partnerMap.set(partnerName, p);

    const s = shipperMap.get(t.shipperName) ?? { name: t.shipperName, volume: 0, ops: new Set<string>(), partners: new Set<string>() };
    s.volume += t.volume; if (t.operation) s.ops.add(t.operation); s.partners.add(partnerName);
    shipperMap.set(t.shipperName, s);
  });
  const partnersSorted = Array.from(partnerMap.values()).sort((a, b) => b.volume - a.volume);
  const shippersSorted = Array.from(shipperMap.values()).sort((a, b) => b.volume - a.volume);
  const sankeyTotal = sankeyData.reduce((s, t) => s + t.volume, 0);
  // 가장 큰 상대 업체 — "** Empty Agent **" 같은 빈 값은 업체가 아니므로 이름 있는 업체 중에서 고른다
  const topPartner = partnersSorted.find(p => p.name !== emptyPartnerLabel);
  const topPartnerShare = sankeyTotal && topPartner ? (topPartner.volume / sankeyTotal) * 100 : 0;
  const hasEmptyPartner = partnersSorted.some(p => p.name === emptyPartnerLabel);
  const hasEmptyShipper = shippersSorted.some(s => s.name === EMPTY_SHIPPER_LABEL);
  // Sankey 가 실제로 잘렸는지 — 잘리지 않았으면 "top 20 …" 안내를 띄우지 않는다
  const sankeyTruncated = partnersSorted.length > SANKEY_MAX_MIDDLE
    || partnersSorted.slice(0, SANKEY_MAX_MIDDLE).some(p => p.shippers.size > SANKEY_MAX_SHIPPERS_PER_MIDDLE);
  // 빈 업체/Shipper 안내 — 실제로 있는 것만
  const emptyLabelNote = hasEmptyPartner && hasEmptyShipper
    ? `Rows labelled <b>${emptyPartnerLabel}</b> / <b>${EMPTY_SHIPPER_LABEL}</b> are shipments with no ${partnerLower} or shipper set on the HBL.`
    : hasEmptyPartner
      ? `Rows labelled <b>${emptyPartnerLabel}</b> are shipments with no ${partnerLower} set on the HBL.`
      : hasEmptyShipper
        ? `Rows labelled <b>${EMPTY_SHIPPER_LABEL}</b> are shipments with no shipper set on the HBL.`
        : "";

  // ---------- Insight 공통 문장 ----------
  const singleOp = opTotals.length === 1;
  const opSentence = !breakdown || topOp.total <= 0 ? ""
    : singleOp ? `All shipments are <b>${topOp.op.label}</b>.`
      : `<b>${topOp.op.label}</b> is the dominant operation at <b>${fmtPct(topOpMix)}</b> of ${isPeriod ? "the period's" : "5-year"} volume.`;
  const partnerSentence = topPartner
    ? (topPartnerShare >= 99.95
      ? `All routed shipments are with <b>${escHtml(topPartner.name)}</b>, across <b>${plural(topPartner.shippers.size, "shipper")}</b>.`
      : `The largest ${partnerLower} is <b>${escHtml(topPartner.name)}</b> with <b>${fmtPct(topPartnerShare)}</b> of routed shipments, across <b>${plural(topPartner.shippers.size, "shipper")}</b>.`)
    : (partnersSorted.length ? `No ${partnerLower} is set on these shipments.` : "");

  const nameCell = (name: string, empty: boolean) =>
    `<div class="name-cell${empty ? " empty" : ""}" title="${escHtml(name)}">${escHtml(name)}</div>`;

  const shareBar = (pct: number) =>
    `<span class="share-bar" style="width:${Math.max(2, Math.round(pct * 0.6))}px"></span>${fmtPct(pct)}`;

  // ---------- KPI / Insight ----------
  // 기간 모드: 전년 동기 데이터가 없으므로 YTD/전년 대비 대신 기간 합계·월평균·주력 Operation 을 보여준다
  const monthCount = monthly.length;
  const monthlyAvg = monthCount ? fiveYearTotal / monthCount : 0;
  const fmtAvg = (v: number) => v.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const peakKpi = peakVolume > 0
    ? `<div class="kpi-num">${fmtInt(peakVolume)}</div><div class="kpi-note">${escHtml(fmtMonthLabel(peakMonth.blDate))}</div>`
    : `<div class="kpi-num">&ndash;</div><div class="kpi-note">No shipments</div>`;
  const partnerKpi = `
      <div class="kpi">
        <div class="kpi-label">${partnerLabel}s / Shippers</div>
        <div class="kpi-num">${fmtInt(partnersSorted.length)} / ${fmtInt(shippersSorted.length)}</div>
        <div class="kpi-note">Distinct partners in the flow</div>
      </div>`;

  const periodKpiHtml = `
    <div class="kpi-grid">
      <div class="kpi">
        <div class="kpi-label">Period Shipments</div>
        <div class="kpi-num">${fmtInt(fiveYearTotal)}</div>
        <div class="kpi-note">${fromStr} &ndash; ${asOfStr}</div>
      </div>
      <div class="kpi blue">
        <div class="kpi-label">Monthly Average</div>
        <div class="kpi-num">${fmtAvg(monthlyAvg)}</div>
        <div class="kpi-note">Over ${plural(monthCount, "month")}</div>
      </div>
      <div class="kpi navy">
        <div class="kpi-label">Top Operation</div>
        <div class="kpi-num">${breakdown && topOp.total > 0 ? fmtPct(topOpMix) : "&ndash;"}</div>
        <div class="kpi-note">${breakdown && topOp.total > 0 ? `${topOp.op.label} &middot; ${plural(topOp.total, "shipment")}` : "No operation breakdown"}</div>
      </div>
      ${partnerKpi}
      <div class="kpi">
        <div class="kpi-label">Peak Month</div>
        ${peakKpi}
      </div>
    </div>`;

  // 전년 동기 비교는 월 단위 — 전년은 기준월 전체, 올해는 기준일까지
  const sameRange = asOfMonth === 1 ? "Jan" : `Jan&ndash;${MONTH_SHORT[asOfMonth - 1]}`;
  const fiveYearKpiHtml = `
    <div class="kpi-grid">
      <div class="kpi">
        <div class="kpi-label">${baseYear}${ytdTag} Shipments</div>
        <div class="kpi-num">${fmtInt(ytdVolume)}</div>
        <div class="kpi-note">Jan 1 &ndash; ${asOfStr}</div>
      </div>
      <div class="kpi blue">
        <div class="kpi-label">vs Same Period ${baseYear - 1}</div>
        <div class="kpi-num ${priorSamePeriod ? deltaCls(ytdDeltaPct) : ""}">${priorSamePeriod ? fmtDelta(ytdDeltaPct) : "&ndash;"}</div>
        <div class="kpi-note">${priorSamePeriod
          ? `${fmtInt(priorSamePeriod)} &rarr; ${fmtInt(ytdVolume)} &middot; ${sameRange}`
          : `No shipments in ${sameRange} ${baseYear - 1}`}</div>
      </div>
      <div class="kpi navy">
        <div class="kpi-label">5-Year Shipments</div>
        <div class="kpi-num">${fmtInt(fiveYearTotal)}</div>
        <div class="kpi-note">${years[0] ?? ""} &ndash; ${baseYear}${ytdTag}</div>
      </div>
      ${partnerKpi}
      <div class="kpi">
        <div class="kpi-label">Peak Month</div>
        ${peakKpi}
      </div>
    </div>`;
  const kpiHtml = isPeriod ? periodKpiHtml : fiveYearKpiHtml;

  const verbPast = isAgentBase ? "handled" : "shipped";
  const nameB = `<b>${escHtml(baseName)}</b>`;
  const periodLead = fiveYearTotal > 0
    ? `${nameB} ${verbPast} <b>${plural(fiveYearTotal, "shipment")}</b> between ${fromStr} and ${asOfStr}, an average of <b>${fmtAvg(monthlyAvg)}</b> per month.`
    : `${nameB} has no shipments between ${fromStr} and ${asOfStr}.`;
  const periodInsightHtml = `
    <div class="insight">
      <span class="tag">Insight</span>
      ${periodLead}
      ${opSentence}
      ${partnerSentence}
      ${peakVolume > 0 && monthCount > 1 ? `The busiest month was <b>${escHtml(fmtMonthLabel(peakMonth.blDate))}</b> at <b>${plural(peakVolume, "shipment")}</b>.` : ""}
      Click a bar in the monthly chart or a node in the Sankey diagram to open the shipment list.
    </div>`;

  const ytdLabel = `${baseYear}${ytdTag}`;
  const fiveYearLead = ytdVolume > 0
    ? `${nameB} ${verbPast} <b>${plural(ytdVolume, "shipment")}</b> in ${ytdLabel}, `
      + (priorSamePeriod
        ? `<b>${fmtDelta(ytdDeltaPct)}</b> against the same period of ${baseYear - 1}.`
        : `with no shipments in the same period of ${baseYear - 1}.`)
    : `${nameB} has no shipments in ${ytdLabel}`
      + (priorSamePeriod ? `, against <b>${plural(priorSamePeriod, "shipment")}</b> in the same period of ${baseYear - 1}.` : ".");
  const fiveYearInsightHtml = `
    <div class="insight">
      <span class="tag">Insight</span>
      ${fiveYearLead}
      ${opSentence}
      ${partnerSentence}
      ${peakYear ? `The strongest year in the window was <b>${yearLabel(peakYear, -1)}</b> at <b>${plural(peakYear.total, "shipment")}</b>.` : ""}
      Click a bar in the monthly chart or a node in the Sankey diagram to open the shipment list.
    </div>`;
  const insightHtml = isPeriod ? periodInsightHtml : fiveYearInsightHtml;

  // ---------- Monthly chart / Year table ----------
  const legendHtml = `
    <div class="chart-legend">
      ${(breakdown ? OPS.filter(op => opTotals.some(x => x.op.code === op.code)) : [OPS[0]])
      .map(op => `<span><span class="sw" style="background:${op.color}"></span>${breakdown ? op.label : "Shipments"}</span>`).join("")}
      <span class="hint">Hover a bar for the breakdown &middot; click to open the shipment list</span>
    </div>`;

  const yearTableRows = yearRows.map((r, i) => {
    const prev = i > 0 ? yearRows[i - 1] : null;
    // 일부 달만 있는 첫 해와는 비교하지 않는다(예: Oct~Dec 대비 +361%)
    // 진행 중인 올해(YTD) 는 전년 전체가 아니라 전년 같은 기간과 비교한다 — KPI 의 "vs Same Period" 와 같은 값
    const isYtdRow = r.year === String(baseYear) && !isYearEnd;
    const yoy = isYtdRow
      ? (priorSamePeriod ? ((r.total - priorSamePeriod) / priorSamePeriod) * 100 : null)
      : prev && prev.total && !isPartialFirstYear(i - 1) ? ((r.total - prev.total) / prev.total) * 100 : null;
    const share = fiveYearTotal ? (r.total / fiveYearTotal) * 100 : 0;
    const opCells = breakdown ? r.byOp.map(v => `<td>${v ? fmtInt(v) : "&ndash;"}</td>`).join("") : "";
    return `
      <tr>
        <td>${yearLabel(r, i)}</td>
        ${opCells}
        <td>${fmtInt(r.total)}</td>
        ${isPeriod ? "" : `<td class="${yoy === null ? "" : deltaCls(yoy)}">${yoy === null ? "&ndash;" : fmtDelta(yoy)}</td>`}
        <td>${fmtPct(share)}</td>
      </tr>`;
  }).join("");
  // 기간 모드는 연도가 잘려 있어(예: 2025-11 ~ 2026-10) 연도 간 YoY 가 의미 없으므로 열을 뺀다
  const yearTotalRow = `
    <tr class="total-row">
      <td>TOTAL</td>
      ${breakdown ? OPS.map((_, i) => `<td>${fmtInt(yearRows.reduce((s, r) => s + r.byOp[i], 0))}</td>`).join("") : ""}
      <td>${fmtInt(fiveYearTotal)}</td>
      ${isPeriod ? "" : "<td></td>"}
      <td>100.0%</td>
    </tr>`;

  // ---------- Monthly detail (최근 12개월) ----------
  // 기간 모드는 기간 안의 모든 달(시작일이 월 중간이면 13개월이 될 수 있음), 5년은 최근 12개월
  const recent = (isPeriod ? [...monthly] : monthly.slice(-12)).reverse();
  const monthRows = recent.map(d => {
    const total = Number(d.volume) || 0;
    const opCells = breakdown ? OPS.map(op => { const v = opVal(d, op.key); return `<td>${v ? fmtInt(v) : "&ndash;"}</td>`; }).join("") : "";
    return `
      <tr>
        <td>${escHtml(fmtMonthLabel(d.blDate))}</td>
        ${opCells}
        <td>${fmtInt(total)}</td>
      </tr>`;
  }).join("");

  // ---------- Partner / Shipper tables ----------
  const TOP_N = 15;
  const partnerRows = partnersSorted.slice(0, TOP_N).map(p => {
    const share = sankeyTotal ? (p.volume / sankeyTotal) * 100 : 0;
    const empty = p.name === emptyPartnerLabel;
    return `
      <tr>
        <td>${nameCell(p.name, empty)}</td>
        <td>${fmtInt(p.volume)}</td>
        <td>${shareBar(share)}</td>
        <td>${fmtInt(p.shippers.size)}</td>
      </tr>`;
  }).join("");
  const partnerRest = partnersSorted.slice(TOP_N);
  const partnerRestRow = partnerRest.length ? `
      <tr>
        <td class="name-cell empty">Other ${plural(partnerRest.length, partnerLower)}</td>
        <td>${fmtInt(partnerRest.reduce((s, p) => s + p.volume, 0))}</td>
        <td>${shareBar(sankeyTotal ? (partnerRest.reduce((s, p) => s + p.volume, 0) / sankeyTotal) * 100 : 0)}</td>
        <td></td>
      </tr>` : "";

  const shipperRows = shippersSorted.slice(0, TOP_N).map(s => {
    const share = sankeyTotal ? (s.volume / sankeyTotal) * 100 : 0;
    const empty = s.name === EMPTY_SHIPPER_LABEL;
    return `
      <tr>
        <td>${nameCell(s.name, empty)}</td>
        <td>${fmtInt(s.volume)}</td>
        <td>${shareBar(share)}</td>
        <td>${fmtInt(s.partners.size)}</td>
      </tr>`;
  }).join("");
  const shipperRest = shippersSorted.slice(TOP_N);
  const shipperRestRow = shipperRest.length ? `
      <tr>
        <td class="name-cell empty">Other ${plural(shipperRest.length, "shipper")}</td>
        <td>${fmtInt(shipperRest.reduce((s, x) => s + x.volume, 0))}</td>
        <td>${shareBar(sankeyTotal ? (shipperRest.reduce((s, x) => s + x.volume, 0) / sankeyTotal) * 100 : 0)}</td>
        <td></td>
      </tr>` : "";

  const opHeader = breakdown ? OPS.map(op => `<th><span class="op-dot" style="background:${op.color}"></span>${op.code}</th>`).join("") : "";

  // ---------- 차트 클릭 드릴다운(숨김 링크 상자) ----------
  const drillBoxes: string[] = [];
  const canDrill = !!asOfIso && baseId > 0;
  // 같은 업체를 반대 기준(Customer ⇄ Agent)으로 새 탭에 연다 — 폴백으로 그려진 화면(notice)에선 반대 기준이 비어 있으니 숨김
  const switchLink = canDrill && canSwitch
    ? `<span class="basis-switch">${buildBroadcastHyperlink(
      `<span class="sw-icon">&#8644;</span>View as ${partnerLabel}`,
      channelName,
      ["BusinessChartDashboard", String(baseId), baseName, dateArg, department, isAgentBase ? "CUSTOMER" : "AGENT"],
      undefined,
      "ifms-bc")}</span>`
    : "";

  const monthDrillIds = monthly.map((d, i) => {
    const present = OPS.filter(op => opVal(d, op.key) > 0).map(op => op.code);
    if (!canDrill || present.length === 0) return null;
    const id = `drill-m-${i}`;
    drillBoxes.push(buildDrillBox(id, fmtMonthLabel(d.blDate), present, channelName, windowName, dateArg, baseCustomerId, "MONTH", baseAgentId, "", d.blDate, baseType));
    return id;
  });

  let nodeSeq = 0;
  const sankeyDrillIdFor = (n: SkNode): string | null => {
    const present = OPS.filter(op => n.ops.has(op.code)).map(op => op.code);
    if (!canDrill || present.length === 0) return null;
    // layer 0 = 기준 업체(추가 조건 없음), layer 1 = 상대 업체(Customer 기준이면 Agent, Agent 기준이면 Customer), layer 2 = Shipper
    let nodeType: string;
    let customerId = baseCustomerId;
    let agentId = baseAgentId;
    if (n.layer === 2) {
      nodeType = "SHIPPER";
    } else if (isAgentBase) {
      nodeType = n.layer === 0 ? "AGENT" : "CUSTOMER";
      if (n.layer === 1) customerId = n.customerId;
    } else {
      nodeType = n.layer === 0 ? "CUSTOMER" : "AGENT";
      if (n.layer === 1) agentId = n.agentId;
    }
    const shipperName = n.layer === 2 && n.name !== EMPTY_SHIPPER_LABEL ? n.name : "";
    const id = `drill-n-${nodeSeq++}`;
    drillBoxes.push(buildDrillBox(id, n.name, present, channelName, windowName, dateArg, customerId, nodeType, agentId, shipperName, "", baseType));
    return id;
  };

  const monthlySvg = buildMonthlyChartSvg(monthly, monthDrillIds, isPeriod);
  const sankeySvg = sankeyData.length ? buildSankeySvg(sankeyData, 1080, baseType, sankeyDrillIdFor) : `<p style="color:${COLORS.faint};padding:20px;">No flow data.</p>`;
  const flowTitle = isAgentBase ? "Agent &rarr; Customer &rarr; Shipper" : "Customer &rarr; Agent &rarr; Shipper";
  // Sankey/막대 영역을 가로로 밀면 메뉴가 노드에서 떨어지니 그때는 닫는다
  const hideMenuOnScroll = `onscroll='(function(){var m=document.getElementById("drill-menu"); if(m) m.style.display="none";})()'`;

  // ---------- 기간 표시 문구 (5년 / 기간 모드) ----------
  const mastheadRight = isPeriod
    ? `Period <b style="color:#fff">${fromStr} &ndash; ${asOfStr}</b>`
    : `As of <b style="color:#fff">${asOfStr}</b> &middot; (5 Years)`;
  const firstMonthLabel = escHtml(fmtMonthLabel(monthly[0]?.blDate ?? ""));
  const lastMonthLabel = escHtml(fmtMonthLabel(monthly[monthly.length - 1]?.blDate ?? ""));
  const chartRange = isPeriod
    ? (monthly.length <= 1 ? firstMonthLabel : `${firstMonthLabel}&ndash;${lastMonthLabel}`)
    : `${years[0] ?? ""}&ndash;${baseYear}`;
  const page2Right = isPeriod
    ? `Page 2 &middot; ${plural(fiveYearTotal, "shipment")} (${fromStr} &ndash; ${asOfStr})`
    : `Page 2 &middot; ${plural(fiveYearTotal, "shipment")} (5 Years)`;
  const page1FootRight = isPeriod
    ? `Covers ${fromStr} &ndash; ${asOfStr}`
    : isYearEnd
      ? `Data as of ${asOfStr}`
      : `${baseYear} YTD reflects partial-year actuals as of ${asOfStr}`;

  return `
    <div class="dash-wrap" onclick='(function(){var m=document.getElementById("drill-menu"); if(m) m.style.display="none";})()'>
      <div id="chart-tooltip" class="chart-tooltip-box"></div>
      <div id="drill-menu" class="drill-menu"></div>
      <div class="drill-boxes">${drillBoxes.join("")}</div>

      <!-- PAGE 1 -->
      <section class="page">
        <div class="masthead">
          <div>
            <div class="eyebrow">IFMS 5.0 &middot; Business Intelligence</div>
            <h1>BUSINESS CHART &middot; ${escHtml(baseName)} <span class="basis">${baseLabel}</span></h1>
            <div class="meta">Type: ${escHtml(department)} | ${baseLabel}: <b>${escHtml(baseName)}</b> | Unit: <b>Shipments (HBL)</b>${switchLink}</div>
          </div>
          <div class="right">
            ${escHtml(companyName)}<br>
            ${mastheadRight}
          </div>
        </div>

        ${notice ? `<div class="note" style="margin:0 0 12px;"><b>Note:</b> ${escHtml(notice)}</div>` : ""}
        ${kpiHtml}
        ${insightHtml}

        <div class="panel">
          <div class="title">
            <h2>${flowTitle} Flow</h2>
            <span class="sub">${plural(sankeyTotal, "shipment")} &middot; ${plural(partnersSorted.length, partnerLower)} &middot; ${plural(shippersSorted.length, "shipper")}${sankeyTruncated ? ` &middot; showing top ${SANKEY_MAX_MIDDLE} ${partnerLower}s, up to ${SANKEY_MAX_SHIPPERS_PER_MIDDLE} shippers each` : ""} &middot; hover a flow for its volume &middot; click a node to open the shipment list</span>
          </div>
          <div class="sankey-wrap" ${hideMenuOnScroll}>${sankeySvg}</div>
        </div>

        <section class="grid2" style="margin-top:12px;">
          <div class="panel">
            <div class="title">
              <h2>Top ${partnerLabel}s</h2>
              <span class="sub">by shipment count</span>
            </div>
            <table class="dash-table">
              <thead><tr><th>${partnerLabel}</th><th>Shipments</th><th>Share</th><th>Shippers</th></tr></thead>
              <tbody>${partnerRows}${partnerRestRow}</tbody>
            </table>
          </div>
          <div class="panel">
            <div class="title">
              <h2>Top Shippers</h2>
              <span class="sub">by shipment count</span>
            </div>
            <table class="dash-table">
              <thead><tr><th>Shipper</th><th>Shipments</th><th>Share</th><th>${partnerLabel}s</th></tr></thead>
              <tbody>${shipperRows}${shipperRestRow}</tbody>
            </table>
          </div>
        </section>

        <div class="note">
          Node height is proportional to shipment count; link thickness shows the volume between two partners.
          ${sankeyTruncated ? `The diagram shows the top <b>${SANKEY_MAX_MIDDLE}</b> ${partnerLower}s by shipment count and, for each, its top <b>${SANKEY_MAX_SHIPPERS_PER_MIDDLE}</b> shippers;
          the tables above count every partner.` : ""}
          ${emptyLabelNote}
          Click a bar or a Sankey node to open the matching shipment list in the main iFMS window;
          when it covers several operations a small menu asks which one to open.
        </div>

        <div class="footnote">
          <div>${escHtml(sourceText) || 'Prepared from "Business Chart" source data'}</div>
          <div>${page1FootRight}</div>
        </div>
      </section>

      <!-- PAGE 2 -->
      <section class="page page-break">
        <div class="masthead">
          <div>
            <div class="eyebrow">IFMS 5.0 &middot; Business Intelligence</div>
            <h1>Monthly Shipment Volume</h1>
            <div class="meta">${chartRange} &middot; stacked by operation</div>
          </div>
          <div class="right">
            ${escHtml(companyName)}<br>
            ${page2Right}
          </div>
        </div>

        <div class="panel">
          <div class="title">
            <h2>Monthly Shipment Volume</h2>
            <span class="sub">${chartRange} &middot; stacked by operation</span>
          </div>
          ${legendHtml}
          <div class="chart-scroll" ${hideMenuOnScroll}>${monthlySvg}</div>
        </div>

        <section class="grid2" style="margin-top:12px;">
          <div class="panel">
            <div class="title">
              <h2>Year Summary</h2>
              <span class="sub">${isPeriod ? "By calendar year within the period" : `5-year cumulative${isYearEnd ? "" : ` &middot; ${baseYear} YTD YoY vs same period`}`}</span>
            </div>
            <table class="dash-table">
              <thead><tr><th>Year</th>${opHeader}<th>Total</th>${isPeriod ? "" : "<th>YoY</th>"}<th>Share</th></tr></thead>
              <tbody>${yearTableRows}${yearTotalRow}</tbody>
            </table>
          </div>

          <div class="panel">
            <div class="title">
              <h2>${isPeriod ? "Months in Period" : "Recent 12 Months"}</h2>
              <span class="sub">Latest month first</span>
            </div>
            <table class="dash-table">
              <thead><tr><th>Month</th>${opHeader}<th>Total</th></tr></thead>
              <tbody>${monthRows}</tbody>
            </table>
          </div>
        </section>

        <div class="footnote">
          <div>${escHtml(sourceText) || 'Prepared from "Business Chart" source data'}</div>
          <div>Printed on ${asOfStr}</div>
        </div>
      </section>

    </div>`;
}

export function openBusinessChartLoadingWindow(windowName: string, comSName: string, targetName?: string): Window | null {
  return openLoadingWindow({
    title: `BUSINESS CHART - ${comSName}`,
    styleText: STYLE,
    windowName,
    targetName,
  });
}

/**
 * @param baseName 기준 업체 이름(Customer 또는 Agent)
 * @param baseId   기준 업체 Id
 * @param baseType "CUSTOMER" | "AGENT" — 실제로 그려지는 기준(리스너에서 폴백됐으면 폴백된 기준)
 * @param notice   폴백 등으로 사용자에게 알릴 문구. 있으면 마스트헤드 아래 배너로 표시하고 View as 링크는 숨긴다
 * @param fromIso  기간 모드 시작일("YYYY-MM-DD"). 비우면 asOfIso 기준 5년 화면(기존 동작)
 */
export function renderBusinessChart(
  win: Window,
  sankeyData: Transaction[],
  monthlyData: MonthlyData[],
  asOfDate: Date,
  asOfIso: string,
  companyName: string,
  baseName: string,
  baseId: number,
  baseType: string,
  department: string,
  channelName: string,
  windowName: string,
  sourceText: string,
  notice: string = "",
  canSwitch: boolean = false,
  fromIso: string = "",
): void {
  const html = buildReportInnerHtml(
    sankeyData, monthlyData, asOfDate, asOfIso,
    companyName, baseName, baseId, baseType, department, channelName, windowName, sourceText, notice, canSwitch, fromIso
  );
  renderReportIntoWindow(win, html);
}